// oxlint-disable actual/no-untranslated-strings -- componente del solo
// ambiente dimostrativo italiano: i testi sono deliberatamente letterali,
// come quelli della visita guidata (public/tour-contabile.js).
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import {
  benvenutoDemoInAttesa,
  consumaBenvenutoDemo,
  segnaTourComeVisto,
} from '#util/demo';

/**
 * Contabile (fork): dialogo di orientamento mostrato a ogni ingresso nel
 * budget dimostrativo — sia dalla landing (`/app.html?demo=1`) sia dal
 * pulsante "Prova la demo" della schermata di benvenuto.
 *
 * Non passa dal gestore dei modali dell'app di proposito: è un affordance
 * del solo ambiente demo e non deve mescolarsi alla coda dei modali veri
 * (per esempio quelli aperti dal budget appena caricato).
 *
 * La visita guidata è servita da `public/tour.js` (motore condiviso, non
 * modificabile) e si raggiunge da `window`, come già fa HelpMenu.
 */
type GDPTour = {
  avvia: () => void;
  dimentica: () => void;
};

function getTour(): GDPTour | undefined {
  return (window as unknown as { GDPTour?: GDPTour }).GDPTour;
}

export function DemoBenvenuto() {
  const [aperto, setAperto] = useState(() => benvenutoDemoInAttesa());

  const esplora = () => {
    consumaBenvenutoDemo();
    // Ha rifiutato il giro guidato qui: non riproporlo in automatico.
    // Resta raggiungibile da Aiuto → "Rivedi la visita guidata".
    segnaTourComeVisto();
    setAperto(false);
  };

  const giroGuidato = () => {
    consumaBenvenutoDemo();
    setAperto(false);
    const tour = getTour();
    if (tour) {
      tour.dimentica();
      tour.avvia();
    }
  };

  useEffect(() => {
    if (!aperto) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        esplora();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto]);

  if (!aperto) return null;

  // Portal su `document.body` + `translateZ(0)`: dentro l'albero dell'app
  // alcune superfici vengono promosse a layer di composizione propri e
  // Chromium le dipinge SOPRA il velo (lo stesso limite documentato per il
  // velo di tour.js). Fuori dall'albero, con un layer proprio, il velo copre
  // davvero tutto.
  return createPortal(
    <View
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-benvenuto-titolo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4500,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backgroundColor: 'rgba(28, 39, 51, 0.45)',
        transform: 'translateZ(0)',
      }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: 430,
          gap: 14,
          padding: '26px 26px 22px',
          borderRadius: 14,
          backgroundColor: theme.modalBackground,
          color: theme.pageText,
          boxShadow: '0 18px 50px rgba(11, 15, 20, 0.35)',
        }}
      >
        <View style={{ gap: 6 }}>
          <Text
            style={{
              alignSelf: 'flex-start',
              backgroundColor: '#D1FAE5',
              color: '#065F46',
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              padding: '3px 10px',
              borderRadius: 999,
            }}
          >
            Ambiente dimostrativo
          </Text>
          <Text
            id="demo-benvenuto-titolo"
            style={{ ...styles.veryLargeText, marginTop: 6 }}
          >
            Dai pure un&rsquo;occhiata in giro
          </Text>
        </View>

        <Text style={{ fontSize: 14, lineHeight: 1.5 }}>
          Questo è un budget di esempio con un anno di movimenti: conti, buste,
          pianificazioni e report già popolati. Resta nel tuo browser — nessun
          account, nessun dato inviato altrove.
        </Text>

        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 6,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="bare"
            style={{ padding: '10px 14px' }}
            onPress={esplora}
          >
            Esplora liberamente
          </Button>
          <Button
            variant="primary"
            autoFocus
            style={{ padding: '10px 18px', fontWeight: 600 }}
            onPress={giroGuidato}
          >
            Fai il giro guidato
          </Button>
        </View>
      </View>
    </View>,
    document.body,
  );
}
